import type { KindeProvider } from "@kinde-oss/kinde-auth-react";
import type { ComponentProps } from "react";

import { NEXT_PAGE_UPON_REDIRECT, SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY } from "@/constants";

type KindeCallbacks = NonNullable<ComponentProps<typeof KindeProvider>["callbacks"]>;

export const KINDE_PROVIDER_CALLBACKS: KindeCallbacks = {
  onSuccess: () => {
    const shouldRedirectUser = localStorage.getItem(SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY) === "true";
    if (shouldRedirectUser) {
      localStorage.setItem(SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY, "false");
      const nextLocation = window.localStorage.getItem(NEXT_PAGE_UPON_REDIRECT);
      if (!nextLocation) return;
      window.localStorage.removeItem(NEXT_PAGE_UPON_REDIRECT);
      window.location.replace(nextLocation);
    }
  },
};

const LEGACY_REFRESH_TOKEN_KEY_PREFIX = "refreshToken";
const KINDE_REFRESH_COOKIE_MARKER = "_kbrte";
const LEGACY_REFRESH_TOKEN_MIGRATION_TIMEOUT_MS = 5000;

const getLegacyRefreshTokenKeys = (): string[] => {
  const keys: string[] = [];
  while (localStorage.getItem(`${LEGACY_REFRESH_TOKEN_KEY_PREFIX}${keys.length}`) !== null) {
    keys.push(`${LEGACY_REFRESH_TOKEN_KEY_PREFIX}${keys.length}`);
  }
  return keys;
};

const hasKindeRefreshCookie = (): boolean => document.cookie.split("; ").some((cookie) => cookie.startsWith(`${KINDE_REFRESH_COOKIE_MARKER}=`));

// Sessions created with useInsecureForRefreshToken only keep the refresh token in localStorage, which the
// cookie mode ignores. Exchanging it once with credentials makes Kinde set the refresh cookie instead.
export async function migrateLegacyRefreshTokenToCookie({ domain, clientId }: { domain: string; clientId: string }): Promise<void> {
  let legacyKeys: string[];
  try {
    legacyKeys = getLegacyRefreshTokenKeys();
  } catch {
    return;
  }
  if (legacyKeys.length === 0) return;

  const removeLegacyRefreshToken = (): void => legacyKeys.forEach((key) => localStorage.removeItem(key));

  if (hasKindeRefreshCookie()) {
    removeLegacyRefreshToken();
    return;
  }

  try {
    await fetch(new URL("/oauth2/token", domain), {
      method: "POST",
      credentials: "include",
      headers: { "Content-type": "application/x-www-form-urlencoded; charset=UTF-8" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: clientId,
        refresh_token: legacyKeys.map((key) => localStorage.getItem(key) ?? "").join(""),
      }).toString(),
      signal: AbortSignal.timeout(LEGACY_REFRESH_TOKEN_MIGRATION_TIMEOUT_MS),
    });
    removeLegacyRefreshToken();
  } catch {
    // Network failures keep the legacy token so the next load can retry.
  }
}
